// [PURPOSE]: Canonical D&D 5e weapon properties data — names + PHB
//   descriptions for tooltips, plus stable id list for picker UIs.
// [WHY]: Phase B / #030 (2026-05-06) — closes B1-4. Pre-#030 the
//   ItemDialog weapon-properties picker rendered the 9 PHB property
//   names as bare checkboxes with no tooltip / explanation. The user
//   reported it as a UX gap during the May 5 smoke test. Promoting
//   the property data + descriptions to a shared module also gives
//   the eventual EditItemDialog weapon-properties picker (parity
//   gap, currently authored only in ItemDialog) a single source of
//   truth to import from when it lands.
// [INTEGRATION]: Imported by `src/components/ItemDialog.js` for the
//   weapon-properties checkbox grid. Picker iterates
//   WEAPON_PROPERTIES and renders each with `title` + `aria-describedby`
//   so the tooltip is available via hover (mouse) and screen readers.
//
// Schema:
//   - id          — stable lowercase id (used for `weaponProperties[]`
//                   storage on items + the registry's polearm / finesse
//                   property checks)
//   - label       — display label (PHB casing)
//   - description — PHB-sourced rules text. Included verbatim so we
//                   match the rulebook the user is reading from.

export const WEAPON_PROPERTIES = Object.freeze([
  {
    id: 'finesse',
    label: 'Finesse',
    description:
      'When making an attack with a finesse weapon, you use your choice of your Strength or Dexterity modifier for the attack and damage rolls. You must use the same modifier for both rolls.',
  },
  {
    id: 'versatile',
    label: 'Versatile',
    description:
      'This weapon can be used with one or two hands. A damage value in parentheses appears with the property — the damage when the weapon is used with two hands to make a melee attack.',
  },
  {
    id: 'two-handed',
    label: 'Two-Handed',
    description:
      'This weapon requires two hands when you attack with it.',
  },
  {
    id: 'light',
    label: 'Light',
    description:
      'A light weapon is small and easy to handle, making it ideal for two-weapon fighting (PHB p.195).',
  },
  {
    id: 'heavy',
    label: 'Heavy',
    description:
      'Small creatures have disadvantage on attack rolls with heavy weapons. A heavy weapon\'s size and bulk make it too large for a Small creature to use effectively.',
  },
  {
    id: 'reach',
    label: 'Reach',
    description:
      'This weapon adds 5 feet to your reach when you attack with it, as well as when determining your reach for opportunity attacks with it.',
  },
  {
    id: 'thrown',
    label: 'Thrown',
    description:
      'If a weapon has the thrown property, you can throw the weapon to make a ranged attack. If the weapon is a melee weapon, you use the same ability modifier for that attack roll and damage roll that you would use for a melee attack with the weapon.',
  },
  {
    id: 'loading',
    label: 'Loading',
    description:
      'Because of the time required to load this weapon, you can fire only one piece of ammunition from it when you use an action, bonus action, or reaction to fire it, regardless of the number of attacks you can normally make.',
  },
  {
    id: 'ammunition',
    label: 'Ammunition',
    description:
      'You can use a weapon that has the ammunition property to make a ranged attack only if you have ammunition to fire from the weapon. Each time you attack with the weapon, you expend one piece of ammunition.',
  },
]);

/**
 * Stable id list for storage / dispatch.
 */
export const WEAPON_PROPERTY_IDS = Object.freeze(
  WEAPON_PROPERTIES.map(p => p.id)
);

/**
 * Lookup by id — returns the property object or undefined.
 */
export function getWeaponProperty(id) {
  if (typeof id !== 'string') return undefined;
  const lower = id.toLowerCase();
  return WEAPON_PROPERTIES.find(p => p.id === lower);
}
